import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import Header from "../components/HeaderB";
import ProfileSidebar from "../components/ProfileSideBar";
import { AuthContext } from "../context/AuthContext";
import purpleMarker from "./marker-icon-2x-violet.png";
import markerShadow from "./marker-shadow.png";
import { MapPin, Navigation, Store } from "lucide-react";

// Custom purple marker
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: purpleMarker,
  iconUrl: purpleMarker,
  shadowUrl: markerShadow,
});

// Reusable form row
const FieldRow = ({ label, children }) => (
  <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 w-full">
    <span className="w-40 font-semibold text-custom-letters text-left shrink-0">{label}:</span>
    <div className="flex-1">{children}</div>
  </div>
);

// Click on map → place marker + reverse-geocode
const LocationMarker = ({ onPick }) => {
  useMapEvents({
    click(e) { onPick(e.latlng.lat, e.latlng.lng); }
  });
  return null;
};

// Smoothly fly map to a new centre
const MapController = ({ center }) => {
  const map = useMap();
  useEffect(() => { if (center) map.flyTo(center, 18); }, [center, map]);
  return null;
};

const AdminEditStore = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken, language } = useContext(AuthContext);

  const translations = {
    el: {
      title: "Επεξεργασία καταστήματος",
      storeNameEl: "Όνομα (ΕΛ)",
      storeNameEn: "Όνομα (EN)",
      cityEl: "Πόλη (ΕΛ)",
      cityEn: "Πόλη (EN)",
      areaEl: "Περιοχή (ΕΛ)",
      areaEn: "Περιοχή (EN)",
      roadEl: "Οδός (ΕΛ)",
      roadEn: "Οδός (EN)",
      latitude: "Γεωγρ. πλάτος",
      longitude: "Γεωγρ. μήκος",
      mapHint: "Κάντε κλικ στον χάρτη για να αλλάξετε τη θέση",
      trackMe: "Εντοπισμός θέσης",
      save: "Αποθήκευση αλλαγών",
      cancel: "Ακύρωση",
      loading: "Φόρτωση...",
      saving: "Αποθήκευση...",
      successTitle: "Αποθηκεύτηκε!",
      successMsg: "Τα στοιχεία του καταστήματος ενημερώθηκαν με επιτυχία.",
      done: "Έτοιμο",
      errorLoad: "Αδυναμία φόρτωσης καταστήματος.",
      errorSave: "Αποτυχία αποθήκευσης.",
      locationError: "Σφάλμα κατά τη λήψη της τοποθεσίας.",
      errorTitle: "Σφάλμα",
      ok: "Εντάξει"
    },
    en: {
      title: "Edit store",
      storeNameEl: "Name (EL)",
      storeNameEn: "Name (EN)",
      cityEl: "City (EL)",
      cityEn: "City (EN)",
      areaEl: "Area (EL)",
      areaEn: "Area (EN)",
      roadEl: "Road (EL)",
      roadEn: "Road (EN)",
      latitude: "Latitude",
      longitude: "Longitude",
      mapHint: "Click on the map to change the location",
      trackMe: "Track me",
      save: "Save changes",
      cancel: "Cancel",
      loading: "Loading...",
      saving: "Saving...",
      successTitle: "Saved!",
      successMsg: "The store information was updated successfully.",
      done: "Done",
      errorLoad: "Failed to load store.",
      errorSave: "Failed to save changes.",
      locationError: "Error getting location.",
      errorTitle: "Error",
      ok: "OK"
    }
  };

  const t = translations[language] || translations.el;

  const [form, setForm] = useState({
    store_name_el: "",
    store_name_en: "",
    city_el: "",
    city_en: "",
    area_el: "",
    area_en: "",
    road_el: "",
    road_en: "",
  });

  const [mapCenter, setMapCenter] = useState({ lat: 37.9838, lng: 23.7275 });
  const [markerPos, setMarkerPos] = useState(null); // { lat, lng }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorPopup, setErrorPopup] = useState(null);

  // ── Load existing store ────────────────────────────────────────────────────
  useEffect(() => {
    if (!accessToken || !id) return;
    const fetchStore = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/adminstores/allstores?lang=${language}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const data = await res.json();
        const store = (data.stores || []).find(s => String(s.store_id) === String(id));

        if (!store) { setErrorPopup({ title: t.errorTitle, msg: t.errorLoad, redirect: "/adminstores" }); return; }

        setForm({
          store_name_el: store.store_name_el || "",
          store_name_en: store.store_name_en || "",
          city_el: store.city_el || "",
          city_en: store.city_en || "",
          area_el: store.area_el || "",
          area_en: store.area_en || "",
          road_el: store.road_el || "",
          road_en: store.road_en || "",
        });

        if (store.latitude && store.longitude) {
          const pos = { lat: Number(store.latitude), lng: Number(store.longitude) };
          setMarkerPos(pos);
          setMapCenter(pos);
        }
      } catch (err) {
        console.error(err);
        setErrorPopup({ title: t.errorTitle, msg: t.errorLoad });
      } finally {
        setLoading(false);
      }
    };
    fetchStore();
  }, [id, accessToken]);

  // ── Map utils ──────────────────────────────────────────────────────────────

  // Reverse-geocode a lat/lng and update the marker
  const fetchAddress = async (lat, lng) => {
    setMarkerPos({ lat, lng });
    setMapCenter({ lat, lng });
    // Update lat/lng fields in parent form
    setForm(prev => ({ ...prev })); // trigger re-render; actual values stored in markerPos
  };

  const handleTrackMe = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => fetchAddress(pos.coords.latitude, pos.coords.longitude),
      () => setErrorPopup({ title: t.errorTitle, msg: t.locationError })
    );
  };

  // ── Form ───────────────────────────────────────────────────────────────────

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        ...form,
        latitude: markerPos?.lat ?? null,
        longitude: markerPos?.lng ?? null,
      };

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/adminstores/${id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      if (!res.ok) throw new Error(t.errorSave);
      setShowSuccess(true);
    } catch (err) {
      setErrorPopup({ title: t.errorTitle, msg: t.errorSave });
    } finally {
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  // if (loading) return (
  //   <div className="flex justify-center items-center h-screen bg-custom-background text-custom-letters">
  //     {t.loading}
  //   </div>
  // );

  const inputClass = "w-full bg-custom-background px-4 py-2 rounded-full outline-none border border-transparent focus:border-custom-grad1 text-custom-letters text-sm";

  const fields = [
    ["store_name_el", t.storeNameEl],
    ["store_name_en", t.storeNameEn],
    ["city_el",       t.cityEl],
    ["city_en",       t.cityEn],
    ["area_el",       t.areaEl],
    ["area_en",       t.areaEn],
    ["road_el",       t.roadEl],
    ["road_en",       t.roadEn],
  ];

  return (
    <div className="min-h-screen bg-custom-background flex flex-col">
      <Header />
      <div className="flex-1 flex flex-row relative text-custom-letters">
        <ProfileSidebar />

        <main id="admineditstore-content" className="flex flex-col flex-1 items-center justify-start px-4 md:px-10 py-10 pt-20 md:pt-10 xl:ml-20">
          <div className="w-full max-w-3xl mx-auto bg-custom-header rounded-3xl shadow-xl p-6 md:p-12">

            {/* Title */}
            <h2 className="text-lg md:text-2xl font-semibold mb-8 text-custom-letters text-center flex justify-center items-center gap-2">
              <Store className="text-custom-grad1" /> {t.title}
            </h2>

            <div className="flex flex-col gap-5">

              {/* Text fields */}
              {fields.map(([name, label]) => (
                <FieldRow key={name} label={label}>
                  <input
                    name={name}
                    value={form[name]}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </FieldRow>
              ))}

              {/* Current coords display */}
              <FieldRow label={t.latitude}>
                <input
                  readOnly
                  value={markerPos?.lat ?? ""}
                  className={inputClass + " opacity-60 cursor-default"}
                />
              </FieldRow>
              <FieldRow label={t.longitude}>
                <input
                  readOnly
                  value={markerPos?.lng ?? ""}
                  className={inputClass + " opacity-60 cursor-default"}
                />
              </FieldRow>

              {/* Map */}
              <div className="mt-4">
                <p className="text-xs text-custom-letters mb-2 text-center opacity-70">
                  {t.mapHint}
                </p>
                <div className="relative rounded-2xl overflow-hidden border-2 border-custom-grad1/30">
                  {/* Track-me button */}
                  <button
                    type="button"
                    onClick={handleTrackMe}
                    title={t.trackMe}
                    className="absolute top-4 right-4 z-[1000] p-3 rounded-full bg-custom-header border border-custom-grad1 shadow"
                  >
                    <Navigation size={20} className="text-custom-letters" />
                  </button>

                  <MapContainer
                    center={[mapCenter.lat, mapCenter.lng]}
                    zoom={18}
                    style={{ height: "380px", width: "100%" }}
                  >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <LocationMarker onPick={fetchAddress} />
                    <MapController center={mapCenter} />
                    {markerPos && (
                      <Marker position={[markerPos.lat, markerPos.lng]} />
                    )}
                  </MapContainer>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex justify-center gap-4 mt-8">
                <button
                  onClick={() => navigate("/adminstores")}
                  className="px-8 py-3 rounded-full bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white font-semibold hover:opacity-90 transition"
                >
                  {t.cancel}
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-8 py-3 rounded-full bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white font-semibold hover:scale-105 transition disabled:opacity-50"
                >
                  {saving ? t.saving : t.save}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Success popup */}
      {showSuccess && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]">
          <div role="dialog" aria-modal="true" aria-labelledby="admineditstore-success-title" className="bg-custom-header rounded-2xl p-6 w-80 shadow-xl text-center">
            <h3 id="admineditstore-success-title" className="text-lg font-semibold text-custom-letters mb-3">{t.successTitle}</h3>
            <p className="text-sm text-custom-letters mb-6">{t.successMsg}</p>
            <button
              onClick={() => navigate("/adminstores")}
              className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white font-bold hover:opacity-90"
            >
              {t.done}
            </button>
          </div>
        </div>
      )}

      {errorPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]">
          <div role="dialog" aria-modal="true" aria-labelledby="admineditstore-error-title" className="bg-custom-header rounded-2xl p-6 w-80 shadow-xl text-center">
            <h3 id="admineditstore-error-title" className="text-lg font-semibold text-custom-letters mb-3">{errorPopup.title}</h3>
            <p className="text-sm text-custom-letters mb-6">{errorPopup.msg}</p>
            <button
              onClick={() => {
                const redirect = errorPopup.redirect;
                setErrorPopup(null);
                if (redirect) navigate(redirect);
              }}
              className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-custom-letters font-bold">
              {t.ok}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEditStore;
